import { AxiosError } from 'axios';
import { useCallback, useContext, useEffect } from 'react';

import type { GridSortModel } from '@mui/x-data-grid';

import { useBooleanState } from '@vyce/core/src/hooks';

import { GRID_PAGE_SIZE } from '@vyce/core/src/constants';
import { useTable } from '@vyce/core/src/hooks/useTable';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { useWidgetErrorHandler } from '@vyce/core/src/hooks/useWidgetErrorHandler';
import { DeviceContext } from '@vyce/core/src/contexts/deviceContext';

import { Props } from '../types';
import { generateExcelFileLink } from '../../../../utils';
import { useInfiniteScroll } from '../../../../hooks/useInfiniteScroll';
import { RecentlyRegisteredDTO } from '../../../../types';
import { useMainDashboardFilter } from '../../../../hooks/useMainDashboardFilter';

const defaultSortModel: GridSortModel = [{ field: 'first_name', sort: 'desc' }];

export const useWidgetData = ({
  selectedCompanyId,
  moduleType,
  mainDashboardFilters,
  fetchRecentlyRegisteredWorkers,
  getRecentlyRegisteredSharedData,
  customTeamId,
  onForbidden,
}: Props) => {
  const [loading, setLoadingTrue, setLoadingFalse] = useBooleanState(false);
  const [downloadButtonLoading, setDownloadButtonLoadingTrue, setDownloadButtonLoadingFalse] =
    useBooleanState(false);
  const { handleServerError } = useContext(NotificationContext);
  // Loading the widget: a 403 hides it instead of raising a toast.
  const handleWidgetError = useWidgetErrorHandler(onForbidden);
  const { isMobile } = useContext(DeviceContext);

  const { total, setTotal, handlePageChange, handleSearchChange } = useTable({
    defaultSortModel,
  });

  const {
    isLoadingList,
    data,
    setNewData,
    setInitialLoadingStatusesFalse,
    setInitialLoadingStatusesTrue,
    resetLoadingList,
  } = useInfiniteScroll<RecentlyRegisteredDTO>([]);

  const {
    selectedLocationOptions,
    unselectedLocationOptions,
    selectedTeamOptions,
    unselectedTeamOptions,
    getTeamsForRequest,
    getLocationsForRequest,
  } = useMainDashboardFilter({
    mainDashboardFilters,
  });

  const prepareModuleData = (moduleType: Props['moduleType']) => {
    if (moduleType === 'time') {
      return { site_ids: getLocationsForRequest(selectedLocationOptions.length ? 'selected' : 'all') };
    }

    if (moduleType === 'teams') {
      return {
        team_ids: getTeamsForRequest(
          customTeamId ? customTeamId : selectedTeamOptions.length ? 'selected' : 'all'
        ),
      };
    }
    return {};
  };

  const getRecentlyRegisteredWorkers = async (offset: number = 0, signal?: AbortSignal) => {
    if (!selectedCompanyId) {
      return;
    }

    const itemStatusMap: Record<number, boolean> = setInitialLoadingStatusesFalse(offset, GRID_PAGE_SIZE);
    setLoadingTrue();
    try {
      const { data } = await fetchRecentlyRegisteredWorkers({
        companyId: selectedCompanyId,
        payload: {
          offset: offset,
          limit: GRID_PAGE_SIZE,
          ...prepareModuleData(moduleType),
        },
        moduleType,
        signal,
      });
      setTotal(data.count);
      setNewData({ newData: data.items, offset });

      setInitialLoadingStatusesTrue({ offset, pageSize: GRID_PAGE_SIZE, itemStatusMap });
      setLoadingFalse();
    } catch (e) {
      const err = e as AxiosError;
      if (err.name !== 'CanceledError') {
        handleWidgetError(err);
        setLoadingFalse();
      }
    }
  };

  const getSharedFile = async () => {
    if (!selectedCompanyId || !getRecentlyRegisteredSharedData) {
      return;
    }
    try {
      setDownloadButtonLoadingTrue();
      const res = await getRecentlyRegisteredSharedData({
        companyId: selectedCompanyId,
        payload: {
          offset: 0,
          limit: GRID_PAGE_SIZE,
          ...prepareModuleData(moduleType),
        },
        moduleType,
      });
      generateExcelFileLink(res);
    } catch (e) {
      handleServerError(e);
    } finally {
      setDownloadButtonLoadingFalse();
    }
  };

  const getPath = useCallback(
    ({ userId, firstName }: { userId: string; firstName: string }) => {
      if (moduleType === 'time') {
        return `/time/users/${firstName}}_${userId}/logs`;
      }

      if (moduleType === 'teams') {
        return `/teams/members/${firstName}}_${userId}`;
      }

      return `/payroll/users/${firstName}}_${userId}`;
    },
    [moduleType]
  );

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    getRecentlyRegisteredWorkers(0, signal);

    return () => {
      controller.abort();
    };
  }, [
    selectedCompanyId,
    selectedLocationOptions,
    unselectedLocationOptions,
    unselectedTeamOptions,
    selectedTeamOptions,
    customTeamId,
  ]);

  return {
    workers: data,
    total,
    loading,
    isMobile,
    downloadButtonLoading,
    isLoadingList,
    getPath,
    getRecentlyRegisteredWorkers,
    resetLoadingList,
    getSharedFile,
    handlePageChange,
    handleSearchChange,
  };
};
